import { type FC } from "react";
import {
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { ProductTypeType } from "@/types/graphql/partner.generated";
import { usePaymentProviderAvailability } from "@/partner/entities/platforms";

// référence: Edit/ProductType/ProductType.js:11-15 StyledToggleButton —
// выбранная кнопка типа продукта закрашена цветом primary (backgroundColor
// !important — иначе Mui-selected даёт только светлый тинт по умолчанию).
// Референс не переопределяет color явно, но default Mui-selected рисует
// текст цветом primary.main — на закрашенном primary-фоне он совпал бы с
// фоном и стал нечитаемым, поэтому докручиваем contrastText (аудит, п.7).
const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  "&.Mui-selected": {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: theme.palette.primary.contrastText,
  },
}));

interface ProductTypeToggleProps {
  value: ProductTypeType;
  onChange: (value: ProductTypeType) => void;
}

/**
 * Переключатель типа продукта (референс: Edit/ProductType/ProductType.js).
 * Рендерится только при создании — смена типа сбрасывает prices (см.
 * ProductForm). Без DONATION — этого типа нет в нашей backend-схеме.
 */
export const ProductTypeToggle: FC<ProductTypeToggleProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  // Гейт — вторая линия обороны (см. usePaymentProviderAvailability):
  // подключены только Paynet и/или PayPal, Stripe нет — они не продлевают
  // подписку, продлевать нечем. Настоящая защита — providersForPrice на
  // бэкенде (цена без провайдера даёт алерт продукта).
  // CCBill подписки поддерживает (продления он шлёт сам, см. renewalStrategy
  // на бэкенде), поэтому подключённый CCBill гейт снимает так же, как Stripe.
  const { stripeConnected, paynetConnected, ccbillConnected, paypalConnected } =
    usePaymentProviderAvailability();
  const subscriptionDisabled =
    (paynetConnected || paypalConnected) &&
    !stripeConnected &&
    !ccbillConnected;

  const tabs: [ProductTypeType, string][] = [
    [ProductTypeType.PpvType, t("products.type.ppv")],
    [ProductTypeType.SubscriptionType, t("products.type.subscription")],
  ];

  const descriptions: Record<ProductTypeType, string> = {
    [ProductTypeType.PpvType]: t("products.form.typeTicketNotice"),
    [ProductTypeType.SubscriptionType]: t(
      "products.form.typeSubscriptionNotice"
    ),
  };

  return (
    <CardContent sx={{ pb: 0 }}>
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={(_event, newValue: ProductTypeType | null) => {
          if (newValue !== null) onChange(newValue);
        }}
      >
        {tabs.map(([tabValue, label]) => {
          const disabled =
            tabValue === ProductTypeType.SubscriptionType &&
            subscriptionDisabled;
          const button = (
            <StyledToggleButton
              key={tabValue}
              value={tabValue}
              disabled={disabled}
            >
              {label}
            </StyledToggleButton>
          );
          if (!disabled) return button;
          return (
            <Tooltip
              key={tabValue}
              title={t("products.form.subscriptionRequiresStripeTooltip")}
            >
              <span>{button}</span>
            </Tooltip>
          );
        })}
      </ToggleButtonGroup>
      <Typography
        color="text.secondary"
        variant="caption"
        display="block"
        sx={{ mt: 0.75 }}
      >
        {descriptions[value]}
      </Typography>
    </CardContent>
  );
};
